// One-time onboarding overlay: a hand performing an upward swipe gesture,
// shown the very first time the next reel becomes swipe-ready during the
// player's first game and nothing else is going on (see PortraitScreen in
// SlotUI.tsx for the trigger logic — first-ever 'ready' transition, only
// while idle). New players have no reason to know that swiping up (rather
// than tapping around for a "next" button) is how you move to the next
// game; this exists purely to teach that gesture once, then gets out of
// the way for good.
//
// `pointerEvents: 'none'` throughout so it can never block the very
// gesture it's demonstrating, even while it's still fading in/out.
//
// The whole overlay also dims the screen behind it a bit — the point is to
// pull focus onto the hand, not just add a hand on top of a fully-lit game.
// The dim is its own layer (`reelay-swipeHintDimIn`, see styles.css) that
// fades its alpha in from 0 over 1s on mount rather than just appearing at
// full strength — a full-strength dim popping in instantly reads as a flash/
// glitch rather than a deliberate "let me show you something" cue. It sits
// at zIndex 0, below the hand/chevrons/text (zIndex 1), so it stays a
// backdrop rather than covering the thing it's supposed to be highlighting.

export interface SwipeHintProps {
  /** True while the fade-out transition is running, just before unmount. */
  fading?: boolean;
}

export function SwipeHint({ fading = false }: SwipeHintProps) {
  return (
    <div
      role="presentation"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 900,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 18,
        pointerEvents: 'none',
        opacity: fading ? 0 : 1,
        transition: 'opacity 400ms ease',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          animation: 'reelay-swipeHintDimIn 1000ms ease forwards',
        }}
      />
      <div style={{ position: 'relative', zIndex: 1, width: 64, height: 100 }}>
        {/* Chevrons travel upward and fade, one after another, suggesting
            continuous motion in the direction the player should swipe. */}
        {[0, 1, 2].map((i) => (
          <svg
            key={i}
            width="28"
            height="16"
            viewBox="0 0 28 16"
            fill="none"
            aria-hidden
            style={{
              position: 'absolute',
              left: '50%',
              marginLeft: -14,
              top: 0,
              animation: 'reelay-swipeHintChevron 1.6s ease-out infinite',
              animationDelay: `${i * 0.35}s`,
            }}
          >
            <path
              d="M2 14 L14 2 L26 14"
              stroke="#fff"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity="0.85"
            />
          </svg>
        ))}
        {/* Hand nudging upward, looping — traced from the app's tap/point
            hand icon (public/images/hand.png) into a plain SVG path so it
            stays crisp at any size instead of upscaling a 30x35 raster. */}
        <svg
          width="40"
          height="50"
          viewBox="0 0 82.31 102.75"
          aria-hidden
          style={{
            position: 'absolute',
            left: '50%',
            marginLeft: -20,
            bottom: 0,
            filter: 'drop-shadow(0 6px 4px rgba(0,0,0,0.35))',
            // Fixed at 3 iterations, matching the reel nudge in SlotUI.tsx
            // (see the comment there) — the whole hint's visible window is
            // timed to exactly 3 * 1.6s so every looping piece finishes a
            // clean final cycle instead of just being masked mid-motion by
            // the container's opacity fade.
            animation: 'reelay-swipeHintHand 1.6s ease-in-out 3',
            animationFillMode: 'forwards',
          }}
        >
          <path
            d="M 21.56,2.62 L 19.50,4.31 L 17.25,7.12 L 16.50,8.81 L 16.12,10.88 L 15.94,42.38 L 15.00,44.06 L 13.31,46.12 L 8.62,50.81 L 3.75,57.56 L 2.44,58.88 L 1.50,59.06 L 1.50,72.38 L 2.44,72.56 L 3.75,74.06 L 4.69,76.69 L 6.56,79.69 L 7.69,82.69 L 9.56,85.69 L 11.25,89.62 L 13.88,93.19 L 15.94,95.25 L 19.31,97.88 L 23.81,99.56 L 24.75,100.50 L 24.94,101.25 L 59.44,101.25 L 59.62,100.31 L 60.38,99.56 L 65.06,97.50 L 67.88,95.25 L 70.31,92.81 L 73.12,88.69 L 74.25,85.12 L 75.75,82.12 L 77.25,76.12 L 78.75,73.12 L 79.50,70.31 L 79.88,67.12 L 80.81,63.19 L 80.81,58.50 L 79.69,53.81 L 78.94,52.12 L 76.31,48.56 L 72.00,44.44 L 68.06,42.00 L 61.31,40.31 L 57.19,39.75 L 52.69,38.62 L 47.62,38.25 L 44.81,37.69 L 42.75,37.69 L 39.75,36.75 L 38.44,35.25 L 37.88,33.00 L 38.06,15.00 L 37.50,9.19 L 36.56,6.56 L 33.19,3.38 L 30.56,2.06 L 26.25,1.50 Z M 24.94,6.19 L 27.75,5.81 L 30.19,6.75 L 32.62,9.38 L 33.38,11.06 L 33.75,13.50 L 33.75,36.38 L 34.50,39.56 L 35.62,40.69 L 37.50,41.25 L 45.56,42.19 L 50.62,43.50 L 53.81,43.69 L 61.69,44.81 L 64.88,45.75 L 69.56,48.00 L 72.94,51.38 L 75.56,55.88 L 76.31,58.50 L 76.12,65.62 L 74.06,71.62 L 72.94,76.88 L 72.38,78.56 L 70.88,81.19 L 69.94,85.12 L 69.19,86.81 L 66.75,90.00 L 62.81,93.75 L 60.94,94.88 L 59.25,95.44 L 52.88,96.38 L 34.12,96.38 L 27.00,95.81 L 22.88,94.69 L 20.81,93.38 L 17.25,90.19 L 13.88,84.75 L 13.12,82.50 L 11.25,79.69 L 9.94,76.12 L 7.88,72.75 L 5.81,67.12 L 6.00,63.94 L 8.06,58.88 L 14.06,51.75 L 14.81,51.38 L 15.38,51.56 L 16.12,52.88 L 16.12,60.75 L 16.69,62.06 L 17.44,62.62 L 18.56,62.62 L 19.69,61.69 L 20.25,60.56 L 20.44,12.56 L 21.00,10.50 L 21.94,9.00 L 24.00,6.75 Z"
            fill="#fff"
            fillRule="evenodd"
          />
        </svg>
      </div>
      <span
        style={{
          position: 'relative',
          zIndex: 1,
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: '#fff',
          textShadow: '0 1px 6px rgba(0,0,0,0.5)',
          textAlign: 'center',
        }}
      >
        Swipe up for the next game
      </span>
    </div>
  );
}
